import React from 'react';

import { Money, formatMoney } from '@betterment/core';
import { Banner, Box, Button, Card, H2, ListRow, Separator, Text, Tooltip } from '@betterment/samba-react';

import { FeeAttempt } from '../data';

interface ReviewSummaryBoxProps {
  feeAttempts: FeeAttempt[];
  bulkFeeAttemptId: string;
  bulkFeeAttemptName: string;
  onSubmit: (bulkFeeAttemptId: string, bulkFeeAttemptName: string) => Promise<void>;
  isSubmitting: boolean;
}

const ReviewSummaryBox: React.FC<ReviewSummaryBoxProps> = ({
  feeAttempts,
  bulkFeeAttemptId,
  bulkFeeAttemptName,
  onSubmit,
  isSubmitting,
}) => {
  const numberOfFees = feeAttempts.length;
  const totalFeeAmount = feeAttempts.reduce((sum, fee) => sum + fee.amount, 0);

  const handleSubmit = async () => {
    await onSubmit(bulkFeeAttemptId, bulkFeeAttemptName);
  };

  return (
    <Card
      css={{
        width: 'container.medium',
        paddingInline: 'space.300',
        borderStyle: 'solid',
        borderWidth: 'thin',
        borderColor: 'border',
        boxShadow: 'none',
      }}
    >
      <Card.Header
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          py: 'space.300',
        }}
      >
        <H2 variant="title200">Summary</H2>
      </Card.Header>
      <Separator />
      <Card.Content>
        <ListRow data-testid="fee-count" css={{ px: '0px' }}>
          <ListRow.LeadingContent>
            <Text>Number of fees</Text>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <Text>{numberOfFees}</Text>
          </ListRow.TrailingContent>
        </ListRow>
        <Separator />
        <ListRow data-testid="total-fees" css={{ px: '0px' }}>
          <ListRow.LeadingContent>
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.100' }}>
              <Text>Total amount</Text>
              <Tooltip>
                <Tooltip.Trigger asChild={true}>
                  <Tooltip.IconButton name="InfoCircle" />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  Fee amount may fluctuate before execution due to market volatility and mutual fund trades.
                </Tooltip.Content>
              </Tooltip>
            </Box>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <Text>{formatMoney(Money(totalFeeAmount))}</Text>
          </ListRow.TrailingContent>
        </ListRow>
        <Separator />
        <ListRow data-testid="estimated-availability" css={{ px: '0px' }}>
          <ListRow.LeadingContent>
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.100' }}>
              <Text>Estimated availability</Text>
              <Tooltip>
                <Tooltip.Trigger asChild={true}>
                  <Tooltip.IconButton name="InfoCircle" />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  Fees submitted after hours, on weekends, or holidays will be placed in the next available business
                  day.
                </Tooltip.Content>
              </Tooltip>
            </Box>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <Text>3-4 business days</Text>
          </ListRow.TrailingContent>
        </ListRow>
        <Separator />
        <Box css={{ mt: 'space.300' }}>
          <Banner variant="warning">
            <Banner.Content>
              <Text as="p" css={{ mb: 'space.200', fontWeight: 'normal' }}>
                Before you submit:
              </Text>
              <Text as="p" css={{ mb: 'space.200' }}>
                To generate cash for the requested fees, we may sell securities in client accounts. These sales don't
                account for gains allowances and could cause clients to exceed their allowance target.
              </Text>
              <Text as="p">
                Clients won't get an email or invoice. Fees will appear in their statements and on their Activity page.
              </Text>
            </Banner.Content>
          </Banner>
        </Box>
      </Card.Content>
      <Card.Footer
        css={{
          py: 'space.300',
        }}
      >
        <Button
          variant="solidPrimary"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting}
          css={{ width: '100%' }}
        >
          Submit fees
        </Button>
      </Card.Footer>
    </Card>
  );
};

export default ReviewSummaryBox;
